import { ColumnSelector } from '@shared/components/ui/ColumnSelector';
import { FiltersDropdown } from '@shared/components/ui/FiltersDropdown';
import { EMPTY_VIEW_ICONS, EmptyView } from '@shared/components/ui/EmptyView';
import { Button } from '@shared/components/ui/button';
import { useQueryParams } from '@shared/hooks/useQueryParams';
import { usePermissions } from '@shared/hooks/usePermissions';
import { useToast } from '@shared/hooks/use-toast';
import { useAuthStore } from '@shared/stores/authStore';
import type { ColumnDef } from '@tanstack/react-table';
import { faFileArrowDown } from '@fortawesome/pro-regular-svg-icons';
import { faLayerGroup } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { useExportLogisticPivot } from '../../hooks/useExportLogisticPivot';
import { useLogisticPivotData } from '../../hooks/useLogisticPivotData';
import {
  LOGISTIC_PIVOT_METRIC_COLUMNS,
  type LogisticPivotDimension,
  type LogisticPivotGroupDisplayMode,
  type LogisticPivotMetricColumnId,
  type LogisticPivotQuery,
} from '../../models/LogisticPivot';
import { DimensionBuilder } from '../interaction/DimensionBuilder';
import { LogisticPivotFiltersPanel, type LogisticPivotFiltersBarState } from '../interaction/LogisticPivotFiltersPanel';
import { LogisticPivotTable } from '../display';
import { parseGroupDisplayParam, serializeGroupDisplayParam } from '../../utils/groupDisplayParam';
import { buildPivotQueryParamConfig } from '../../utils/pivotQueryParamsConfig';
import { parseDimensionsParam, serializeDimensionsParam } from '../../utils/parseDims';
import { sortPivotRows, type PivotSortKey } from '../../utils/sortPivotRows';
import { cn } from '@shared/utils/utils';

const DEFAULT_DIMENSIONS: LogisticPivotDimension[] = ['year', 'month'];

/** Criterio de fecha fijo para el informe (sin selector en UI). */
const PIVOT_DATE_COLUMN = 'processed_at' as const;

const formatDateLocal = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

export interface LogisticPivotMainProps {
  /**
   * Prefijo de claves en la URL (p. ej. `lp` → `lp_start_date`) para embeber el informe en Logística
   * sin pisar `start_date` / `end_date` del informe principal.
   */
  queryParamKeyPrefix?: string;
  /** Cuando va dentro de la página de Logística: sin padding horizontal duplicado. */
  embedded?: boolean;
}

export const Main = ({ queryParamKeyPrefix, embedded = false }: LogisticPivotMainProps = {}) => {
  const { t } = useTranslation('logisticPivotModule');
  const { toast } = useToast();
  const { getTeamType } = usePermissions();
  const isTenant3pl = getTeamType() === 'tenant_3pl';
  const exportMutation = useExportLogisticPivot();

  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const lastErrorShown = useRef<string | null>(null);

  const getDefaultStartDate = () => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return formatDateLocal(d);
  };
  const getDefaultEndDate = () => formatDateLocal(new Date());

  const pivotQueryConfig = useMemo(
    () =>
      buildPivotQueryParamConfig(queryParamKeyPrefix, {
        start: getDefaultStartDate(),
        end: getDefaultEndDate(),
      }),

    [queryParamKeyPrefix],
  );

  const { params: queryParams, updateParams: updateQueryParams } = useQueryParams(pivotQueryConfig);
  const [searchParams, setSearchParams] = useSearchParams();

  /** Quitar `date_column` / `lp_date_column` antiguos de la URL (ya no se usan). */
  const strippedLegacyDateColumn = useRef(false);
  useEffect(() => {
    if (strippedLegacyDateColumn.current) return;
    const legacyKey = queryParamKeyPrefix ? `${queryParamKeyPrefix}_date_column` : 'date_column';
    if (!searchParams.has(legacyKey)) return;
    strippedLegacyDateColumn.current = true;
    const next = new URLSearchParams(searchParams);
    next.delete(legacyKey);
    setSearchParams(next, { replace: true });
  }, [queryParamKeyPrefix, searchParams, setSearchParams]);

  const [dimensions, setDimensions] = useState<LogisticPivotDimension[]>(() => {
    const parsed = parseDimensionsParam(queryParams.dims, DEFAULT_DIMENSIONS);
    const canTeam = useAuthStore.getState().getTeamType() === 'tenant_3pl';
    return canTeam ? parsed : parsed.filter((d) => d !== 'team');
  });

  const [groupDisplay, setGroupDisplay] = useState<LogisticPivotGroupDisplayMode>(() =>
    parseGroupDisplayParam(queryParams.pivot_view, 'hierarchy'),
  );

  const [startDate, setStartDate] = useState(queryParams.start_date ?? getDefaultStartDate());
  const [endDate, setEndDate] = useState(queryParams.end_date ?? getDefaultEndDate());
  const [teamId, setTeamId] = useState<number | null>(() => {
    if (useAuthStore.getState().getTeamType() !== 'tenant_3pl') return null;
    return queryParams.team_id ?? null;
  });
  const [paymentMethodId, setPaymentMethodId] = useState<number | null>(queryParams.payment_method_id ?? null);
  const [shopId, setShopId] = useState<number | null>(queryParams.shop_id ?? null);
  const [courierId, setCourierId] = useState<number | null>(queryParams.courier_id ?? null);
  const [countryId, setCountryId] = useState<number | null>(queryParams.country_id ?? null);
  const [courierServiceId, setCourierServiceId] = useState<number | null>(queryParams.courier_service_id ?? null);

  const activeOptionalFiltersCount = useMemo(() => {
    let n = 0;
    if (isTenant3pl && teamId != null) n++;
    if (paymentMethodId != null) n++;
    if (shopId != null) n++;
    if (courierId != null) n++;
    if (countryId != null) n++;
    if (courierServiceId != null) n++;
    return n;
  }, [isTenant3pl, teamId, paymentMethodId, shopId, courierId, countryId, courierServiceId]);

  const groupingBadgeAria = useMemo(
    () => t('dimensions.grouping_badge_aria', { count: dimensions.length }),
    [t, dimensions.length],
  );

  useEffect(() => {
    const parsed = parseDimensionsParam(queryParams.dims, DEFAULT_DIMENSIONS);
    const next = isTenant3pl ? parsed : parsed.filter((d) => d !== 'team');
    setDimensions(next);
    if (!isTenant3pl && parsed.includes('team')) {
      updateQueryParams({ dims: serializeDimensionsParam(next) });
    }
  }, [queryParams.dims, isTenant3pl, updateQueryParams]);

  useEffect(() => {
    setGroupDisplay(parseGroupDisplayParam(queryParams.pivot_view, 'hierarchy'));
  }, [queryParams.pivot_view]);

  useEffect(() => {
    if (dimensions.length <= 1 && groupDisplay === 'hierarchy') {
      setGroupDisplay('columns');
      updateQueryParams({ pivot_view: serializeGroupDisplayParam('columns') });
    }
  }, [dimensions.length, groupDisplay, updateQueryParams]);

  useEffect(() => {
    if (queryParams.start_date) setStartDate(queryParams.start_date);
  }, [queryParams.start_date]);
  useEffect(() => {
    if (queryParams.end_date) setEndDate(queryParams.end_date);
  }, [queryParams.end_date]);
  useEffect(() => {
    if (!isTenant3pl) {
      setTeamId(null);
      if (queryParams.team_id != null) {
        updateQueryParams({ team_id: null });
      }
      return;
    }
    setTeamId(queryParams.team_id ?? null);
  }, [queryParams.team_id, isTenant3pl, updateQueryParams]);
  useEffect(() => {
    setPaymentMethodId(queryParams.payment_method_id ?? null);
  }, [queryParams.payment_method_id]);
  useEffect(() => {
    setShopId(queryParams.shop_id ?? null);
  }, [queryParams.shop_id]);
  useEffect(() => {
    setCourierId(queryParams.courier_id ?? null);
  }, [queryParams.courier_id]);
  useEffect(() => {
    setCountryId(queryParams.country_id ?? null);
  }, [queryParams.country_id]);
  useEffect(() => {
    setCourierServiceId(queryParams.courier_service_id ?? null);
  }, [queryParams.courier_service_id]);

  const apiQuery: LogisticPivotQuery = useMemo(() => {
    const q: LogisticPivotQuery = {
      start_date: startDate,
      end_date: endDate,
      date_column: PIVOT_DATE_COLUMN,
      dimensions: dimensions.length > 0 ? dimensions : undefined,
    };
    if (isTenant3pl && teamId != null) q.team_id = teamId;
    if (paymentMethodId != null) q.payment_method_id = paymentMethodId;
    if (shopId != null) q.shop_id = shopId;
    if (courierId != null) q.courier_id = courierId;
    if (countryId != null) q.country_id = countryId;
    if (courierServiceId != null) q.courier_service_id = courierServiceId;
    return q;
  }, [
    startDate,
    endDate,
    dimensions,
    teamId,
    paymentMethodId,
    shopId,
    courierId,
    countryId,
    courierServiceId,
    isTenant3pl,
  ]);

  const { rows, isLoading, isFetching, error } = useLogisticPivotData({ query: apiQuery });

  const handleExportExcel = useCallback(() => {
    exportMutation.mutate(apiQuery, {
      onSuccess: (url) => {
        window.open(url, '_blank');
        toast({
          title: t('export.success.title'),
          description: t('export.success.description'),
          variant: 'default',
        });
      },
      onError: (err: Error) => {
        toast({
          title: t('export.error.title'),
          description: err?.message || t('export.error.unexpected'),
          variant: 'destructive',
        });
      },
    });
  }, [apiQuery, exportMutation, t, toast]);

  useEffect(() => {
    if (error && error !== lastErrorShown.current) {
      lastErrorShown.current = error;
      toast({
        title: t('toasts.error.title'),
        description: error || t('toasts.error.unexpected'),
        variant: 'destructive',
      });
    }
    if (!error) lastErrorShown.current = null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error]);

  useEffect(() => {
    if (!isLoading && isInitialLoad) setIsInitialLoad(false);
  }, [isLoading, isInitialLoad]);

  const handleFiltersChange = useCallback(
    (f: LogisticPivotFiltersBarState) => {
      if (f.startDate !== undefined) setStartDate(f.startDate);
      if (f.endDate !== undefined) setEndDate(f.endDate);
      if (f.teamId !== undefined) setTeamId(f.teamId);
      if (f.paymentMethodId !== undefined) setPaymentMethodId(f.paymentMethodId);
      if (f.shopId !== undefined) setShopId(f.shopId);
      if (f.courierId !== undefined) setCourierId(f.courierId);
      if (f.countryId !== undefined) setCountryId(f.countryId);
      if (f.courierServiceId !== undefined) setCourierServiceId(f.courierServiceId);

      updateQueryParams({
        start_date: f.startDate,
        end_date: f.endDate,
        team_id: f.teamId,
        payment_method_id: f.paymentMethodId,
        shop_id: f.shopId,
        courier_id: f.courierId,
        country_id: f.countryId,
        courier_service_id: f.courierServiceId,
      });
    },
    [updateQueryParams],
  );

  const handleDimensionsChange = useCallback(
    (next: LogisticPivotDimension[]) => {
      setDimensions(next);
      updateQueryParams({ dims: serializeDimensionsParam(next) });
    },
    [updateQueryParams],
  );

  const handleGroupDisplayChange = useCallback(
    (next: LogisticPivotGroupDisplayMode) => {
      setGroupDisplay(next);
      updateQueryParams({ pivot_view: serializeGroupDisplayParam(next) });
    },
    [updateQueryParams],
  );

  const metricColumns: ColumnDef<{ id: string }>[] = useMemo(
    () =>
      LOGISTIC_PIVOT_METRIC_COLUMNS.map((m) => ({
        id: m.id,
        header: t(m.headerKey),
      })),
    [t],
  );

  const columnStorageKey = useMemo(
    () => (queryParamKeyPrefix ? `logistic-pivot-metrics-v2-${queryParamKeyPrefix}` : 'logistic-pivot-metrics-v2'),
    [queryParamKeyPrefix],
  );

  const [selectedMetricColumns, setSelectedMetricColumns] = useState<string[]>(
    LOGISTIC_PIVOT_METRIC_COLUMNS.map((m) => m.id),
  );

  const visibleMetrics = useMemo(
    () =>
      selectedMetricColumns.filter((id): id is LogisticPivotMetricColumnId =>
        LOGISTIC_PIVOT_METRIC_COLUMNS.some((m) => m.id === id),
      ),
    [selectedMetricColumns],
  );

  /** Un solo estado evita `setSortDir` dentro del updater de `setSortKey` (en Strict Mode el updater se puede ejecutar dos veces y el doble toggle dejaba siempre `asc`). */
  const [pivotSort, setPivotSort] = useState<{
    key: PivotSortKey | null;
    dir: 'asc' | 'desc';
  }>({ key: null, dir: 'asc' });

  const sortKey = pivotSort.key;
  const sortDir = pivotSort.dir;

  const handleSortChange = useCallback((key: PivotSortKey) => {
    setPivotSort((prev) => {
      if (prev.key !== key) {
        return { key, dir: 'asc' };
      }
      return { key: prev.key, dir: prev.dir === 'asc' ? 'desc' : 'asc' };
    });
  }, []);

  const sortedRows = useMemo(() => {
    if (!pivotSort.key || rows.length <= 1) return rows;
    return sortPivotRows(rows, pivotSort.key, pivotSort.dir);
  }, [rows, pivotSort.key, pivotSort.dir]);

  useEffect(() => {
    if (!pivotSort.key) return;
    if (pivotSort.key.startsWith('dim:')) {
      const d = pivotSort.key.slice(4) as LogisticPivotDimension;
      if (!dimensions.includes(d)) setPivotSort({ key: null, dir: 'asc' });
      return;
    }
    if (pivotSort.key.startsWith('metric:')) {
      const id = pivotSort.key.slice(7) as LogisticPivotMetricColumnId;
      if (!visibleMetrics.includes(id)) setPivotSort({ key: null, dir: 'asc' });
    }
  }, [dimensions, visibleMetrics, pivotSort.key]);

  const hasErrorNoData = error && rows.length === 0;

  const shellClass = cn('w-full min-w-0 space-y-4 animate-pulse', embedded ? 'py-4' : 'px-4 py-4');

  if (isLoading && isInitialLoad) {
    return (
      <div className={shellClass}>
        <div className="flex gap-2">
          <div className="h-9 w-24 bg-muted rounded-md" />
          <div className="h-9 w-24 bg-muted rounded-md" />
          <div className="h-9 w-28 bg-muted rounded-md" />
        </div>
        <div className="h-40 bg-muted/70 rounded-md border border-border/60" />
      </div>
    );
  }

  return (
    <div
      className={cn('w-full min-w-0 space-y-3', embedded ? 'py-4' : 'px-4 py-4')}
      id={embedded ? 'logistic-pivot-dynamic' : undefined}
    >
      <div className="flex flex-wrap items-center gap-2">
        <FiltersDropdown
          title={t('filters.title')}
          activeFiltersCount={activeOptionalFiltersCount}
          triggerTestId="logistic-pivot-filters-toggle"
          dropdownContentClassName="sm:min-w-[700px] sm:max-w-[720px] p-4 bg-body-bg text-foreground border border-card-border"
        >
          <LogisticPivotFiltersPanel
            startDate={startDate}
            endDate={endDate}
            teamId={teamId}
            paymentMethodId={paymentMethodId}
            shopId={shopId}
            courierId={courierId}
            countryId={countryId}
            courierServiceId={courierServiceId}
            onFiltersChange={handleFiltersChange}
            getDefaultStartDate={getDefaultStartDate}
            getDefaultEndDate={getDefaultEndDate}
            showTeamFilter={isTenant3pl}
          />
        </FiltersDropdown>
        <FiltersDropdown
          title={t('dimensions.grouping_title')}
          triggerLabel={t('dimensions.grouping_title')}
          triggerIcon={faLayerGroup}
          activeFiltersCount={dimensions.length}
          activeFiltersAriaLabel={groupingBadgeAria}
          triggerTestId="logistic-pivot-grouping-toggle"
          dropdownContentClassName="sm:min-w-[420px] sm:max-w-[520px] p-4 sm:p-5 bg-body-bg text-foreground border border-card-border"
        >
          <DimensionBuilder
            value={dimensions}
            onChange={handleDimensionsChange}
            groupDisplay={groupDisplay}
            onGroupDisplayChange={handleGroupDisplayChange}
            allowTeamDimension={isTenant3pl}
          />
        </FiltersDropdown>
        <ColumnSelector
          columns={metricColumns}
          selected={selectedMetricColumns}
          onColumnsChange={setSelectedMetricColumns}
          storageKey={columnStorageKey}
          dataTestId="logistic-pivot-column-selector"
          reorderable
          reorderSectionTitle={t('filters.metrics_column_order')}
        />
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="gap-1.5"
          disabled={exportMutation.isPending}
          data-testid="logistic-pivot-export-excel"
          aria-label={t('export.aria')}
          onClick={handleExportExcel}
        >
          <FontAwesomeIcon icon={faFileArrowDown} className="h-4 w-4" aria-hidden />
          <span className="hidden sm:inline">
            {exportMutation.isPending ? t('export.generating') : t('export.button')}
          </span>
          <span className="sm:hidden">{exportMutation.isPending ? '…' : t('export.button_short')}</span>
        </Button>
      </div>

      <div className="min-w-0" data-testid="logistic-pivot-section">
        {hasErrorNoData ? (
          <EmptyView
            type="emptyState"
            icon={EMPTY_VIEW_ICONS.ORDERS}
            title={t('empty.error.title')}
            description={t('empty.error.description')}
          />
        ) : rows.length === 0 && !isFetching ? (
          <EmptyView
            type="emptyState"
            icon={EMPTY_VIEW_ICONS.ORDERS}
            title={t('empty.no_data.title')}
            description={t('empty.no_data.description')}
          />
        ) : (
          <LogisticPivotTable
            rows={sortedRows}
            dimensions={dimensions}
            visibleMetrics={visibleMetrics}
            groupDisplay={groupDisplay}
            isFetching={isFetching}
            sortKey={sortKey}
            sortDir={sortDir}
            onSortChange={handleSortChange}
          />
        )}
      </div>
    </div>
  );
};
